import React, { useState } from "react";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { EditCard } from "icons/dashboard/editCard";
import { VisaIcon } from "icons/payments/VisaIcon";
import { CrownCard } from "icons/dashboard/CrownCard";
import { DeleteCard } from "icons/dashboard/DeleteCard";
import CustomTooltip from "components/ui-component/CustomTooltip";
import PaymentMethodActionsMenu from "./PaymentMethodActionsMenu";
import { CrownMenuIcon } from "icons/payments/CrownMenuIcon";
import { EditMenuIcon } from "icons/payments/EditMenuIcon";
import { DeleteMenuIcon } from "icons/payments/DeleteMenuIcon";
import JCB from "../../icons/payments/JCB.svg";
import dinersClub from "../../icons/payments/dinersClub.svg";
import amex from "../../icons/payments/amex.svg";
import Discover from "icons/payments/Discover";
import UnionPay from "icons/payments/UnionPay";
import { useTid } from "utils/testid";
import { formatCardExpiry } from "utils/DateFormat";

type Val = any;

interface Props {
  val: Val;
  idx: number;
  onDelete: () => void;
  onEdit: () => void;
  onDefault: () => void;
  isMobile?: boolean;
}

const PaymentMethodCard: React.FC<Props> = ({
  val,
  idx,
  onDelete,
  onEdit,
  onDefault,
  isMobile = false,
}) => {
  const tid = useTid();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const cardId = val?.id || val?.payment_card_id || val?.cc_4digit || idx;
  const cardLabel = `Card ending in ${val?.cc_4digit?.slice(-4) || "unknown"}`;

  // Brand marks ship at different intrinsic sizes (Visa is a short wordmark,
  // JCB and AMEX are much taller). The design gives them a single 20px-high
  // band, so cap every mark to that height and let the width follow — without
  // it the taller marks overflow the row and none of them line up.
  // The design gives the brand mark a 36x22 box; cap every mark to that
  // height and let the width follow its own aspect ratio.
  const brandMarkClass = "max-h-[22px] xl:max-h-5 w-auto object-contain";

  const imgUrl = (): React.ReactNode => {
    const cardType = val?.cc_type;
    switch (cardType?.toLowerCase()) {
      case "visa":
        return <VisaIcon fill="white" className={brandMarkClass} />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="AMEX" className={brandMarkClass} />;
      case "mc":
      case "master":
      case "mastercard":
        return <MastercardIcon className="h-[22px] xl:h-5 w-auto" />;
      case "discover":
      case "ds":
        return <Discover className={brandMarkClass} />;
      case "jcb":
        return <img src={JCB} alt="JCB" className={brandMarkClass} />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="DC" className={brandMarkClass} />;
      case "unionpay":
        return <UnionPay className={brandMarkClass} />;
      default:
        return "";
    }
  };

  return (
    <div
      role="group"
      aria-label={cardLabel}
      data-testid={tid(`card-method-${cardId}`)}
      className={`relative ${isMenuOpen ? "z-[5]" : ""} ${
        val?.available ? "portalBackgroundCard" : "backgroundCardUnavailable"
      } rounded-[16px] shadow-[0_12px_12px_rgba(15,38,92,0.17)] h-[200px] cursor-pointer w-[350px] max-w-full xl:rounded-[12px] xl:shadow-none xl:h-[150px] xl:w-[260px] group animate__animated animate__fadeIn`}
    >
      <div className="absolute top-0 left-0 flex rounded-tl-[16px] xl:rounded-tl-[12px]">
        {val?.isExpired ? (
          <>
            <div className="bg-red w-3 rounded-tl-[16px] xl:rounded-tl-[12px] xl:group-hover:opacity-30"></div>
            <div
              className="bg-red py-[2px] pr-[15px] text-white text-xs leading-[normal] xl:leading-normal font-medium xl:group-hover:opacity-30"
              style={{ clipPath: "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)" }}
            >
              Expired
            </div>
          </>
        ) : (
          <div className="h-5"></div>
        )}
        {!val?.available ? (
          <>
            {!val?.isExpired ? (
              <div className="bg-yellow w-3 rounded-tl-[16px] xl:rounded-tl-[12px] xl:group-hover:opacity-30"></div>
            ) : null}

            <div
              style={{
                clipPath: val?.isExpired
                  ? "polygon(8% 0, 100% 0%, 90% 99%, 0% 100%)"
                  : "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
              }}
              className={`bg-yellow text-white text-xs leading-[normal] xl:leading-normal flex items-center font-medium ${
                !val?.isExpired ? "pr-[15px]" : "py-[2px] px-[15px] -ml-2"
              } xl:group-hover:opacity-30`}
            >
              Unavailable
            </div>
          </>
        ) : (
          <div className="h-5"></div>
        )}
        {val?.available && val?.default_profile === "Y" ? (
          <>
            {!val?.isExpired ? (
              <div className="bg-iconObscure w-3 rounded-tl-[16px] xl:rounded-tl-[12px] xl:group-hover:opacity-30"></div>
            ) : null}
            <div
              className={`bg-iconObscure text-white text-xs leading-[normal] xl:leading-normal flex items-center font-medium ${
                !val?.isExpired ? "pr-[15px]" : "py-[2px] px-[15px] -ml-2"
              } xl:group-hover:opacity-30`}
              style={{
                clipPath: val?.isExpired
                  ? "polygon(8% 0, 100% 0%, 90% 99%, 0% 100%)"
                  : "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
              }}
            >
              Default
            </div>
          </>
        ) : (
          <div className="h-5"></div>
        )}
        {val?.available && val?.isExpireSoon ? (
          <>
            {!val?.isExpired && val?.default_profile !== "Y" ? (
              <div className="bg-yellow w-3 rounded-tl-[16px] xl:rounded-tl-[12px] xl:group-hover:opacity-30"></div>
            ) : null}
            <div
              className={`bg-yellow text-white text-xs leading-[normal] xl:leading-normal flex items-center font-medium ${
                val?.isExpired || val?.default_profile === "Y"
                  ? "py-[2px] px-[15px] -ml-2"
                  : "pr-[15px]"
              } xl:group-hover:opacity-30`}
              style={{
                clipPath:
                  val?.isExpired || val?.default_profile === "Y"
                    ? "polygon(8% 0, 100% 0%, 90% 99%, 0% 100%)"
                    : "polygon(0 0, 100% 0%, 86% 100%, 0% 100%)",
              }}
            >
              Expires Soon
            </div>
          </>
        ) : (
          <div className="h-5"></div>
        )}
      </div>
      {/* Below xl the card follows the 350x200 Figma redesign: brand mark 24px
          in from the top-right, text rows at x=24 / x=180 on the 84 / 136 / 154
          baselines. From xl up the `xl:` overrides restore the original 260x150
          desktop card, which its own Figma frames still specify. */}
      <div className="absolute top-6 right-6 h-[22px] xl:top-5 xl:right-5 xl:h-5 flex items-center xl:group-hover:opacity-30">
        {imgUrl()}
      </div>

      <p className="absolute left-6 top-[84px] whitespace-pre text-[18px] leading-[normal] font-semibold tracking-[0.36px] text-white xl:left-5 xl:top-[60px] xl:text-xs xl:leading-normal xl:font-medium xl:tracking-normal xl:group-hover:text-txtSecondary">
        <span className="xl:hidden">{`XXXX  XXXX  XXXX  ${
          val?.cc_4digit?.slice(-4) || "-- --"
        }`}</span>
        <span className="hidden xl:inline">{`XXXX XXXX XXXX ${
          val?.cc_4digit?.slice(-4) || "-- --"
        }`}</span>
      </p>

      <p className="absolute left-6 top-[136px] text-[10px] leading-[normal] font-medium tracking-[0.4px] text-white/70 xl:left-5 xl:top-[92px] xl:text-xs xl:leading-normal xl:font-normal xl:tracking-normal xl:text-white xl:group-hover:text-txtSecondary">
        <span className="xl:hidden">CARD HOLDER</span>
        <span className="hidden xl:inline">NAME</span>
      </p>
      <p className="absolute left-6 top-[154px] text-sm leading-[normal] font-semibold capitalize text-white xl:left-5 xl:top-[112px] xl:leading-normal xl:font-medium xl:group-hover:text-txtSecondary">
        {val?.account_name || "--"}
      </p>

      <p className="absolute left-[180px] top-[136px] text-[10px] leading-[normal] font-medium tracking-[0.4px] text-white/70 xl:left-[175px] xl:top-[92px] xl:text-xs xl:leading-normal xl:font-normal xl:tracking-normal xl:text-white xl:group-hover:text-txtSecondary">
        VALID THRU
      </p>
      <p className="absolute left-[180px] top-[154px] text-sm leading-[normal] font-semibold text-white xl:left-[175px] xl:top-[112px] xl:leading-normal xl:font-medium xl:tracking-[0.28px] xl:group-hover:text-txtSecondary">
        {formatCardExpiry(val?.cc_expiry)}
      </p>
      <PaymentMethodActionsMenu
        testId={`card-method-${cardId}`}
        triggerLabel={`Actions for ${cardLabel}`}
        onOpenChange={setIsMenuOpen}
        actions={[
          ...(!val?.isExpired && val?.default_profile !== "Y" && val?.available
            ? [
                {
                  key: "set-default",
                  label: "Set as Default",
                  icon: <CrownMenuIcon />,
                  onClick: onDefault,
                },
              ]
            : []),
          ...(!val?.isExpired && val?.available
            ? [
                {
                  key: "edit",
                  label: "Edit Card",
                  icon: <EditMenuIcon />,
                  onClick: onEdit,
                },
              ]
            : []),
          {
            key: "delete",
            label: "Delete Card",
            icon: <DeleteMenuIcon />,
            onClick: onDelete,
            isDestructive: true,
            dividerBefore: !val?.isExpired && val?.available,
          },
        ]}
      />

      <div className="absolute bottom-2 z-[2] right-2 cursor-pointer hidden gap-2 xl:group-hover:flex">
        <div
          role="button"
          tabIndex={0}
          aria-label={`Delete ${cardLabel}`}
          data-testid={tid(`card-method-${cardId}-delete-button`)}
          onClick={onDelete}
        >
          <CustomTooltip
            maxwidth="430px"
            place="top"
            id={`card-delete-${cardId}`}
            content={`<div class="text-xs leading-[18px] font-normal">Delete</div>`}
            disabled={isMobile}
          >
            <DeleteCard
              id={`card-delete-${cardId}`}
              height="34"
              width="34"
              aria-hidden="true"
            />
          </CustomTooltip>
        </div>
        {!val?.isExpired && val?.available && (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Edit ${cardLabel}`}
            data-testid={tid(`card-method-${cardId}-edit-button`)}
            onClick={onEdit}
          >
            <CustomTooltip
              maxwidth="430px"
              place="top"
              id={`card-edit-${cardId}`}
              content={`<div class="text-xs leading-[18px] font-normal">Edit</div>`}
              disabled={isMobile}
            >
              <EditCard
                id={`card-edit-${cardId}`}
                height="34"
                width="34"
                aria-hidden="true"
              />
            </CustomTooltip>
          </div>
        )}
        {!val?.isExpired && val?.default_profile !== "Y" && val?.available ? (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Set ${cardLabel} as default`}
            data-testid={tid(`card-method-${cardId}-default-button`)}
            onClick={onDefault}
          >
            <CustomTooltip
              maxwidth="430px"
              place="top"
              id={`card-default-${cardId}`}
              content={`<div class="text-xs leading-[18px] font-normal">Set as default</div>`}
              disabled={isMobile}
            >
              <CrownCard
                id={`card-default-${cardId}`}
                height="34"
                width="34"
                aria-hidden="true"
              />
            </CustomTooltip>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentMethodCard;
